import { useCallback, useMemo } from 'react'
import type { CardLayout } from '../Gantt.utils'
import type { GanttChartProps } from '../GanttChart.types'

type GanttArrow = NonNullable<GanttChartProps['props']['arrows']>[number]

type UseGanttArrowsLayoutArgs = {
  arrows: GanttArrow[]
  cards: GanttChartProps['props']['cards']
  cardLayoutMap: Record<string, CardLayout>
  cardOrderMap: Record<string, number>
}

const useGanttArrowsLayout = ({
  arrows,
  cards,
  cardLayoutMap,
  cardOrderMap
}: UseGanttArrowsLayoutArgs) => {
  const compareArrowsByTargetCard = useCallback(
    (firstArrow: GanttArrow, secondArrow: GanttArrow) => {
      const firstTarget = cardLayoutMap[firstArrow.toCardId]
      const secondTarget = cardLayoutMap[secondArrow.toCardId]
      if (!firstTarget || !secondTarget) return 0

      if (firstTarget.top !== secondTarget.top)
        return firstTarget.top - secondTarget.top
      if (firstTarget.left !== secondTarget.left)
        return firstTarget.left - secondTarget.left
      return (
        (cardOrderMap[firstArrow.toCardId] ?? 0) -
        (cardOrderMap[secondArrow.toCardId] ?? 0)
      )
    },
    [cardLayoutMap, cardOrderMap]
  )

  const orderedArrows = useMemo(() => {
    const sourceOrder = cards.reduce<Record<string, number>>(
      (accumulator, card, index) => {
        if (accumulator[card.id] === undefined) {
          accumulator[card.id] = index
        }
        return accumulator
      },
      {}
    )

    return [...arrows].sort((firstArrow, secondArrow) => {
      const firstSourceOrder =
        sourceOrder[firstArrow.fromCardId] ?? Number.MAX_SAFE_INTEGER
      const secondSourceOrder =
        sourceOrder[secondArrow.fromCardId] ?? Number.MAX_SAFE_INTEGER
      if (firstSourceOrder !== secondSourceOrder)
        return firstSourceOrder - secondSourceOrder

      return compareArrowsByTargetCard(firstArrow, secondArrow)
    })
  }, [cards, arrows, compareArrowsByTargetCard])

  const sourceSpineXMap = useMemo(() => {
    const spineBySourceCardId: Record<string, number> = {}
    const arrowsBySource: Record<string, GanttArrow[]> = {}

    orderedArrows.forEach((arrow) => {
      if (!arrowsBySource[arrow.fromCardId]) {
        arrowsBySource[arrow.fromCardId] = []
      }
      arrowsBySource[arrow.fromCardId].push(arrow)
    })

    Object.entries(arrowsBySource).forEach(([sourceCardId, sourceArrows]) => {
      const source = cardLayoutMap[sourceCardId]
      if (!source) return

      const targetLefts = sourceArrows
        .map((arrow) => cardLayoutMap[arrow.toCardId]?.left)
        .filter((left): left is number => typeof left === 'number')
      if (!targetLefts.length) return

      const nearestTargetLeft = Math.min(...targetLefts)
      const leftmostCardEdge = Math.min(source.left, nearestTargetLeft)
      spineBySourceCardId[sourceCardId] = Math.max(8, leftmostCardEdge - 12)
    })

    return spineBySourceCardId
  }, [orderedArrows, cardLayoutMap])

  return { orderedArrows, sourceSpineXMap }
}

export default useGanttArrowsLayout
